import React from 'react';
import { Phone, Mail, MapPin, Facebook, Instagram, Linkedin } from 'lucide-react';
import { companyInfo } from '../mockData';

const Footer = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-slate-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <img 
              src={companyInfo.logo} 
              alt={companyInfo.name}
              className="h-16 w-auto mb-6"
            />
            <p className="text-gray-400 mb-6 leading-relaxed">
              Da oltre 50 anni leader nel settore della sicurezza a Roma. Qualità, professionalità e Made in Italy.
            </p>
            {/* Italian Flag */}
            <div className="flex gap-2">
              <div className="w-16 h-1.5 bg-green-600 rounded-full"></div>
              <div className="w-16 h-1.5 bg-white rounded-full"></div>
              <div className="w-16 h-1.5 bg-red-600 rounded-full"></div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-6">Link Rapidi</h3>
            <ul className="space-y-3">
              <li>
                <button 
                  onClick={() => scrollToSection('home')}
                  className="text-gray-400 hover:text-green-400 transition-colors"
                >
                  Home
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('about')}
                  className="text-gray-400 hover:text-green-400 transition-colors"
                >
                  Chi Siamo
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('services')}
                  className="text-gray-400 hover:text-green-400 transition-colors"
                >
                  Servizi
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('gallery')}
                  className="text-gray-400 hover:text-green-400 transition-colors"
                >
                  Progetti
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('faq')}
                  className="text-gray-400 hover:text-green-400 transition-colors"
                >
                  FAQ
                </button>
              </li>
              <li>
                <a 
                  href="/privacy-policy"
                  className="text-gray-400 hover:text-green-400 transition-colors"
                >
                  Privacy & Cookie Policy
                </a>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-bold mb-6">I Nostri Servizi</h3>
            <ul className="space-y-3 text-gray-400">
              <li>Serrande Avvolgibili</li>
              <li>Porte Sezionali</li>
              <li>Tapparelle</li>
              <li>Cancelli</li>
              <li>Automazioni</li>
              <li>Manutenzione 24/7</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-bold mb-6">Contatti</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin size={20} className="text-green-400 flex-shrink-0 mt-1" />
                <span className="text-gray-400 text-sm leading-relaxed">
                  {companyInfo.address}
                </span>
              </li>
              <li>
                <a 
                  href={`tel:${companyInfo.phone}`}
                  className="flex items-center gap-3 text-gray-400 hover:text-green-400 transition-colors"
                >
                  <Phone size={20} className="text-green-400" />
                  <div>
                    <div className="text-xs text-gray-500">Cell:</div>
                    <span>{companyInfo.phone}</span>
                  </div>
                </a>
              </li>
              <li>
                <a 
                  href={`tel:${companyInfo.office}`}
                  className="flex items-center gap-3 text-gray-400 hover:text-green-400 transition-colors"
                >
                  <Phone size={20} className="text-green-400" />
                  <div>
                    <div className="text-xs text-gray-500">Fisso:</div>
                    <span>{companyInfo.office}</span>
                  </div>
                </a>
              </li>
              <li>
                <a 
                  href={`mailto:${companyInfo.email}`}
                  className="flex items-center gap-3 text-gray-400 hover:text-green-400 transition-colors break-all"
                >
                  <Mail size={20} className="text-green-400" />
                  <span className="text-sm">{companyInfo.email}</span>
                </a>
              </li>
            </ul>

            {/* Social Media */}
            <div className="mt-6">
              <h4 className="text-sm font-semibold mb-3">Seguici</h4>
              <div className="flex gap-3">
                <a 
                  href="https://www.facebook.com/share/188HvBgest/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-slate-800 p-2.5 rounded-lg hover:bg-green-600 transition-colors"
                  aria-label="Facebook"
                >
                  <Facebook size={20} />
                </a>
                <a 
                  href="https://www.instagram.com/italserrandesrl?igsh=aXg3MGU1ajJwYWR2"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-slate-800 p-2.5 rounded-lg hover:bg-green-600 transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram size={20} />
                </a>
                <a 
                  href="https://www.linkedin.com" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-slate-800 p-2.5 rounded-lg hover:bg-green-600 transition-colors"
                  aria-label="LinkedIn"
                >
                  <Linkedin size={20} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col gap-4">
            {/* Copyright and Links */}
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-400">
              <div>
                © {new Date().getFullYear()} ITAL SERRANDE S.r.l.
              </div>
              <div className="flex gap-6">
                <a href="/privacy-policy" className="hover:text-green-400 transition-colors">
                  Privacy & Cookie Policy
                </a>
                <a href="#" className="hover:text-green-400 transition-colors">
                  Termini e Condizioni
                </a>
              </div>
            </div>
            
            {/* Legal Information */}
            <div className="text-xs text-gray-500 text-center md:text-left leading-relaxed space-y-2">
              <div>
                <strong className="text-gray-400">ITAL SERRANDE S.r.l.</strong> | 
                Sede Legale: Via Castino, 29 - 00166 Roma | 
                C.F. / P. IVA: 09270881007 | 
                C.C.I.A.A. (REA): 1152580 | 
                Cap. Soc.: 10.000,00 € i.v.
              </div>
              <div>
                <a 
                  href="/privacy-policy" 
                  className="text-green-400 hover:text-green-300 underline font-medium"
                >
                  Privacy Policy
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
